import { type Component, Show } from 'solid-js'
import Container from '~/components/Container'
import Icon from '~/components/Icon'
import { useAuth } from '~/contexts/AuthContext'
import { SITE } from '~/data/site'

const Footer: Component = () => {
  const { isAuthenticated } = useAuth()
  return (
    <footer class="relative bg-linear-to-br from-surface-muted via-surface to-surface-muted border-t border-border">
      {/* Background Pattern */}
      <div class="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.05),transparent_50%)] dark:bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.03),transparent_50%)]" />

      <Container class="relative py-16">
        {/* Main Content */}
        <div class="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
          {/* Brand Section */}
          <div class="lg:col-span-5">
            <div class="flex items-center gap-4 mb-6">
              <div class="p-3 rounded-2xl bg-linear-to-br from-primary-500 to-primary-600 shadow-lg shadow-primary-500/25">
                <Icon icon="edens:logo" class="text-white" size={28} />
              </div>
              <div>
                <h3 class="text-2xl font-bold text-foreground">{SITE.name}</h3>
                <p class="text-sm text-foreground-subtle font-medium">{SITE.tagline}</p>
              </div>
            </div>

            <p class="text-foreground-muted leading-relaxed mb-8 text-lg">
              Transformamos ideas innovadoras en productos digitales que marcan la diferencia. Desarrollo de software, aplicaciones móviles y plataformas escalables.
            </p>

            {/* Contact Info */}
            <address class="not-italic space-y-4">
              {[
                { icon: 'material-symbols-light:call-outline', label: 'Teléfono', value: SITE.contact.phone, href: SITE.contact.phoneHref },
                { icon: 'material-symbols-light:mail-outline', label: 'Email', value: SITE.contact.email, href: SITE.contact.emailHref },
                { icon: 'material-symbols-light:location-on-outline', label: 'Ubicación', value: SITE.contact.location }
              ].map(item => (
                <div class="flex items-center gap-4 group">
                  <div class="p-2 rounded-xl bg-surface group-hover:bg-primary-soft transition-colors">
                    <Icon icon={item.icon} size={20} class="text-primary" />
                  </div>
                  <div class="min-w-0">
                    <p class="text-sm font-medium text-foreground">{item.label}</p>
                    {item.href ? (
                      <a
                        href={item.href}
                        class="text-foreground-muted hover:text-primary transition-colors break-all rounded-sm focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500"
                      >
                        {item.value}
                      </a>
                    ) : (
                      <p class="text-foreground-muted">{item.value}</p>
                    )}
                  </div>
                </div>
              ))}
            </address>
          </div>

          {/* Services & Links */}
          <div class="lg:col-span-7">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Servicios */}
              <div>
                <h4 class="text-lg font-bold mb-6 text-foreground flex items-center gap-2">
                  <div class="w-1 h-6 bg-linear-to-b from-primary-500 to-primary-600 rounded-full" />
                  Servicios
                </h4>
                <ul class="space-y-3">
                  {[
                    { name: 'Desarrollo Web & Apps', href: '/#servicios' },
                    { name: 'Cloud & DevOps', href: '/#servicios' },
                    { name: 'IA & Automatización', href: '/#servicios' },
                    { name: 'UX/UI Design', href: '/#servicios' },
                    { name: 'Seguridad', href: '/#servicios' }
                  ].map(service => (
                    <li>
                      <a href={service.href} class="group flex items-center gap-3 text-foreground-muted hover:text-primary transition-all duration-200">
                        <div class="w-1.5 h-1.5 rounded-full bg-border-strong group-hover:bg-primary-500 transition-colors" />
                        {service.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Empresa */}
              <div>
                <h4 class="text-lg font-bold mb-6 text-foreground flex items-center gap-2">
                  <div class="w-1 h-6 bg-linear-to-b from-primary-500 to-primary-600 rounded-full" />
                  Empresa
                </h4>
                <ul class="space-y-3">
                  {[
                    { name: 'Sobre Nosotros', href: '/#nosotros' },
                    { name: 'Nuestro Equipo', href: '/#nosotros' },
                    { name: 'UI Components', href: '/componentes' },
                    { name: 'Contacto', href: '/#contacto' }
                  ].map(link => (
                    <li>
                      <a href={link.href} class="group flex items-center gap-3 text-foreground-muted hover:text-primary transition-all duration-200">
                        <div class="w-1.5 h-1.5 rounded-full bg-border-strong group-hover:bg-primary-500 transition-colors" />
                        {link.name}
                      </a>
                    </li>
                  ))}
                  <Show when={isAuthenticated()}>
                    <li>
                      <a href="/admin" class="group flex items-center gap-3 text-foreground-muted hover:text-primary transition-all duration-200">
                        <div class="w-1.5 h-1.5 rounded-full bg-border-strong group-hover:bg-primary-500 transition-colors" />
                        Panel de administración
                      </a>
                    </li>
                  </Show>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Social Links */}
        <div class="flex justify-center mb-12">
          <div class="flex items-center gap-2 p-2 rounded-2xl bg-surface/60 dark:bg-slate-800/60 backdrop-blur-sm border border-border shadow-lg">
            {[
              {
                icon: 'mdi:github',
                href: SITE.social.github,
                label: 'GitHub',
                color: 'hover:text-foreground',
                external: true
              },
              {
                icon: 'mdi:whatsapp',
                href: SITE.contact.whatsapp,
                label: 'WhatsApp',
                color: 'hover:text-green-600',
                external: true
              },
              {
                icon: 'mdi:email',
                href: SITE.contact.emailHref,
                label: `Escribir a ${SITE.contact.email}`,
                color: 'hover:text-primary-600',
                external: false
              }
            ].map(social => (
              <a
                href={social.href}
                target={social.external ? '_blank' : undefined}
                rel={social.external ? 'noopener noreferrer' : undefined}
                class={`p-3 rounded-xl text-foreground-muted ${social.color} transition-all duration-200 hover:bg-surface-hover hover:shadow-md group focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500`}
                title={social.label}
                aria-label={social.label}
              >
                <Icon icon={social.icon} size={20} class="group-hover:scale-110 transition-transform" />
              </a>
            ))}
          </div>
        </div>

        {/* Bottom Bar */}
        <div class="pt-8 border-t border-border">
          <div class="flex flex-col md:flex-row justify-between items-center gap-6">
            <div class="flex items-center gap-4">
              <p class="text-foreground-subtle text-sm">
                © {new Date().getFullYear()} {SITE.legalName}
              </p>
              <div class="hidden md:block w-1 h-1 rounded-full bg-border-strong" />
              <p class="text-foreground-subtle text-sm">{SITE.tagline}</p>
            </div>
          </div>
        </div>
      </Container>
    </footer>
  )
}

export default Footer
